import {
  DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS,
  safeExternalURL,
  type LibraryFormat,
  type LibraryLane,
  type LibraryTriageConfidence,
  type LibraryTriageDecision,
} from "./domain";

export interface LibraryTriageInput {
  sourceURL: string;
  trustedFormat?: LibraryFormat;
  hasTrustedTextMetadata?: boolean;
  videoDurationSeconds?: number;
  longVideoThresholdSeconds?: number;
}

interface FormatResult {
  format: LibraryFormat;
  confidence: LibraryTriageConfidence;
}

export function classifyLibraryTriage(
  input: LibraryTriageInput,
): LibraryTriageDecision {
  const threshold = input.longVideoThresholdSeconds
    ?? DEFAULT_LONG_VIDEO_THRESHOLD_SECONDS;
  if (!Number.isInteger(threshold) || threshold <= 0) {
    throw new Error("The long-video threshold must be a positive whole second.");
  }
  const formatResult = classifyFormat(input);
  switch (formatResult.format) {
    case "text":
      return decision(formatResult, "read", threshold);
    case "audio":
      return decision(formatResult, "listen", threshold);
    case "video": {
      const duration = input.videoDurationSeconds;
      if (
        duration === undefined
        || !Number.isInteger(duration)
        || duration < 0
      ) {
        return decision(
          {
            format: "video",
            confidence: "requires_review",
          },
          null,
          threshold,
        );
      }
      return decision(
        formatResult,
        duration <= threshold ? "read" : "watch",
        threshold,
      );
    }
    case "unknown":
      return decision(formatResult, null, threshold);
  }
}

export function correctedLibraryTriage(
  automatic: LibraryTriageDecision,
  format: LibraryFormat,
  lane: LibraryLane,
): LibraryTriageDecision {
  return {
    ...automatic,
    format,
    lane,
    requiresReview: false,
    wasHumanCorrected: true,
  };
}

export function reviewedLibraryTriage(
  automatic: LibraryTriageDecision,
  format: LibraryFormat,
  lane: LibraryLane,
): LibraryTriageDecision {
  return (
    !automatic.requiresReview
    && automatic.format === format
    && automatic.lane === lane
  )
    ? automatic
    : correctedLibraryTriage(automatic, format, lane);
}

function classifyFormat(input: LibraryTriageInput): FormatResult {
  if (input.trustedFormat !== undefined) {
    return {
      format: input.trustedFormat,
      confidence: input.trustedFormat === "unknown"
        ? "requires_review"
        : "high",
    };
  }

  const sourceURL = safeExternalURL(input.sourceURL);
  const host = sourceURL ? new URL(sourceURL).hostname.toLocaleLowerCase() : "";
  if (AUDIO_HOSTS.has(host)) {
    return { format: "audio", confidence: "medium" };
  }
  if (VIDEO_HOSTS.has(host)) {
    return { format: "video", confidence: "medium" };
  }
  if (input.hasTrustedTextMetadata) {
    return { format: "text", confidence: "high" };
  }
  return { format: "unknown", confidence: "requires_review" };
}

function decision(
  detected: FormatResult,
  lane: LibraryLane | null,
  longVideoThresholdSeconds: number,
): LibraryTriageDecision {
  return {
    detectedFormat: detected.format,
    detectedLane: lane,
    format: detected.format,
    lane,
    ruleVersion: "library-triage-v1",
    confidence: detected.confidence,
    requiresReview: lane === null || detected.confidence === "requires_review",
    longVideoThresholdSeconds,
    wasHumanCorrected: false,
  };
}

const AUDIO_HOSTS = new Set([
  "app.snipd.com",
  "podcasts.apple.com",
  "snipd.com",
]);

const VIDEO_HOSTS = new Set([
  "m.youtube.com",
  "vimeo.com",
  "www.youtube.com",
  "youtu.be",
  "youtube.com",
]);
